import { type ReactElement } from 'react';
import {
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';

type TestingEntityMetricColumnWidths = {
  dateTime: number;
  metric: number;
  name: number;
  project: number;
  status: number;
  tags: number;
};

const testingEntityMetricColumnWidthsByCount = {
  0: { name: 36, status: 14, dateTime: 20, metric: 0, tags: 30, project: 0 },
  1: { name: 32, status: 12, dateTime: 16, metric: 18, tags: 22, project: 0 },
  2: { name: 29, status: 11, dateTime: 15, metric: 14, tags: 17, project: 0 },
  3: { name: 24, status: 10, dateTime: 14, metric: 12, tags: 16, project: 0 },
  4: { name: 18, status: 9, dateTime: 15, metric: 10.5, tags: 16, project: 0 },
} satisfies Record<number, TestingEntityMetricColumnWidths>;

const testingEntityProjectColumnWidthsByCount = {
  0: { name: 30, status: 11, dateTime: 16, metric: 0, tags: 22, project: 21 },
  1: { name: 29, status: 10, dateTime: 14, metric: 14, tags: 17, project: 16 },
  2: { name: 26, status: 9, dateTime: 13, metric: 12, tags: 14, project: 14 },
  3: { name: 21, status: 9, dateTime: 12, metric: 11, tags: 13, project: 12 },
  4: { name: 18, status: 8, dateTime: 12, metric: 10, tags: 12, project: 10 },
} satisfies Record<number, TestingEntityMetricColumnWidths>;

const resolveTestingEntityMetricColumnWidths = (
  visibleMetricCount: number,
  includeProject: boolean,
): TestingEntityMetricColumnWidths => {
  const widthsByCount = includeProject
    ? testingEntityProjectColumnWidthsByCount
    : testingEntityMetricColumnWidthsByCount;

  switch (visibleMetricCount) {
    case 0:
      return widthsByCount[0];
    case 1:
      return widthsByCount[1];
    case 2:
      return widthsByCount[2];
    case 3:
      return widthsByCount[3];
    case 4:
    default:
      return widthsByCount[4];
  }
};

export const buildEntityMetricColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
  includeActions = true,
): ReactElement => {
  const widths = resolveTestingEntityMetricColumnWidths(visibleMetricKeys.length, includeProject);

  return (
    <colgroup>
      <col width={tableV2ChevronColumnWidth} />
      <col width={`${String(widths.name)}%`} />
      <col width={`${String(widths.status)}%`} />
      <col width={`${String(widths.dateTime)}%`} />
      {visibleMetricKeys.map((key) => (
        <col key={key} width={`${String(widths.metric)}%`} />
      ))}
      <col width={`${String(widths.tags)}%`} />
      {includeProject ? <col width={`${String(widths.project)}%`} /> : null}
      {includeActions ? <col width={tableV2ActionsColumnWidth} /> : null}
    </colgroup>
  );
};

export const buildTestingEntityMetricColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
  includeActions = true,
): ReactElement => buildEntityMetricColgroup(visibleMetricKeys, includeProject, includeActions);
